import { inject } from '@angular/core';
import { type Routes } from '@angular/router';

import { ContestRole, SystemRole } from '../models/User';

import { activePoolGuard } from './guards/active-pool.guard';
import { contestRedirectGuard } from './guards/contest-redirect.guard';
import { redirectToRoleRoute, userRoleGuard } from './guards/user-role.guard';
import { AuthService } from './services/auth.service';

export const routes: Routes = [
  {
    path: '',
    loadComponent: () =>
      import('./components/wrapper/wrapper.component').then((m) => m.WrapperComponent),
    children: [
      {
        path: '',
        canActivate: [redirectToRoleRoute],
        loadComponent: () =>
          import('./components/home/home.component').then((m) => m.HomeComponent),
        data: { description: 'home.subtitle' },
      },
      {
        path: 'login',
        canActivate: [redirectToRoleRoute],
        data: { description: 'login.accessCodePrompt' },
        loadComponent: () =>
          import('./components/login/login.component').then((m) => m.LoginComponent),
      },
      {
        path: 'register',
        canActivate: [redirectToRoleRoute],
        data: { description: 'register.subtitle' },
        loadComponent: () =>
          import('./components/register/register.component').then((m) => m.RegisterComponent),
      },
      {
        path: 'verify',
        loadComponent: () =>
          import('./components/activate/activate.component').then((m) => m.ActivateComponent),
      },

      {
        path: 'forgot-password',
        canActivate: [redirectToRoleRoute],
        data: { description: 'forgotPassword.subtitle' },
        loadComponent: () =>
          import('./components/forgot-password/forgot-password.component').then(
            (m) => m.ForgotPasswordComponent,
          ),
      },
      {
        path: 'settings',
        canActivate: [userRoleGuard],
        data: { breadcrumb: 'settings.pageTitle', icon: 'settings', helpKey: 'settings' },
        loadComponent: () =>
          import('./components/settings/settings.component').then((m) => m.SettingsComponent),
      },
      {
        path: 'account',
        canActivate: [userRoleGuard],
        data: { breadcrumb: 'wrapper.accountAction', icon: 'account_circle', helpKey: 'account' },
        loadComponent: () =>
          import('./components/account/account.component').then((m) => m.AccountComponent),
      },
      {
        path: 'admin',

        canActivate: [userRoleGuard],
        data: {
          roles: [SystemRole.SuperAdmin],
          breadcrumb: 'quickActions.adminDashboard',
          icon: 'admin_panel_settings',
        },
        children: [
          {
            path: '',
            data: { helpKey: 'admin-dashboard' },
            loadComponent: () =>
              import('./components/admin-dashboard/admin-dashboard.component').then(
                (m) => m.AdminDashboardComponent,
              ),
          },
          {
            path: 'mailer-templates',
            data: {
              breadcrumb: 'adminDashboard.mailerTemplates',
              icon: 'mail',
              helpKey: 'mailer-template-preview',
            },
            loadComponent: () =>
              import('./components/mailer-template-preview/mailer-template-preview.component').then(
                (m) => m.MailerTemplatePreviewComponent,
              ),
          },
          {
            path: 'preview-components',
            data: {
              breadcrumb: 'adminDashboard.previewComponents',
              icon: 'preview',
              helpKey: 'component-preview',
            },
            loadComponent: () =>
              import('./components/component-preview/component-preview.component').then(
                (m) => m.ComponentPreviewComponent,
              ),
          },
          {
            path: 'manage',
            loadComponent: () =>
              import('./components/admin-contest-management/admin-contest-management').then(
                (m) => m.AdminContestManagementComponent,
              ),
            children: [
              {
                path: '',
                redirectTo: '/admin',
                pathMatch: 'full',
              },
              {
                path: 'users',
                data: {
                  breadcrumb: 'nounForms.user.plural',
                  icon: 'people',
                  showFilters: true,
                  isStandalonePage: true,
                  helpKey: 'user-list',
                },
                loadComponent: () =>
                  import('./components/user-list/user-list.component').then(
                    (m) => m.UserListComponent,
                  ),
              },
              {
                path: 'contests',
                data: {
                  breadcrumb: 'nounForms.contest.plural',
                  icon: 'emoji_events',
                  isStandalonePage: true,
                  helpKey: 'contest-list',
                },
                loadComponent: () =>
                  import('./components/contest-list/contest-list.component').then(
                    (m) => m.ContestListComponent,
                  ),
              },
              {
                path: 'organizers',
                data: {
                  breadcrumb: 'nounForms.organizer.plural',
                  icon: 'manage_accounts',
                  showFilters: true,
                  isStandalonePage: true,
                  helpKey: 'organizer-list',
                },
                loadComponent: () =>
                  import('./components/organizer-list/organizer-list.component').then(
                    (m) => m.OrganizerListComponent,
                  ),
              },
              {
                path: 'dancers',
                data: {
                  breadcrumb: 'nounForms.dancer.plural',
                  icon: 'accessibility_new',
                  showFilters: true,
                  isStandalonePage: true,
                  helpKey: 'dancer-list',
                },
                loadComponent: () =>
                  import('./components/dancer-list/dancer-list.component').then(
                    (m) => m.DancerListComponent,
                  ),
              },
              {
                path: 'judges',
                data: {
                  breadcrumb: 'nounForms.judge.plural',
                  icon: 'gavel',
                  showFilters: true,
                  isStandalonePage: true,
                  helpKey: 'judge-list',
                },
                loadComponent: () =>
                  import('./components/judge-list/judge-list.component').then(
                    (m) => m.JudgeListComponent,
                  ),
              },
              {
                path: 'pools',
                data: {
                  breadcrumb: 'nounForms.pool.plural',
                  icon: 'pools',
                  showFilters: true,
                  isStandalonePage: true,
                  helpKey: 'pool-list',
                },
                loadComponent: () =>
                  import('./components/pool-list/pool-list.component').then(
                    (m) => m.PoolListComponent,
                  ),
              },
              {
                path: 'scores',
                data: {
                  breadcrumb: 'nounForms.score.plural',
                  icon: 'score',
                  showFilters: true,
                  isStandalonePage: true,
                  helpKey: 'score-list',
                },
                loadComponent: () =>
                  import('./components/score-list/score-list.component').then(
                    (m) => m.ScoreListComponent,
                  ),
              },
              {
                path: 'rounds',
                data: {
                  breadcrumb: 'nounForms.round.plural',
                  icon: 'format_list_numbered',
                  showFilters: true,
                  isStandalonePage: true,
                  helpKey: 'round-list',
                },
                loadComponent: () =>
                  import('./components/round-list/round-list.component').then(
                    (m) => m.RoundListComponent,
                  ),
              },
            ],
          },
        ],
      },
      {
        path: 'c/:contestId',
        redirectTo: 'contests/:contestId',
      },
      {
        path: 'contests',
        canActivate: [userRoleGuard],
        data: {
          roles: [SystemRole.SuperAdmin, SystemRole.Admin, SystemRole.User],
          breadcrumb: 'nounForms.contest.plural',
          icon: 'emoji_events',
        },
        children: [
          {
            path: '',
            canActivate: [userRoleGuard],
            data: {
              roles: [SystemRole.SuperAdmin, SystemRole.Admin, SystemRole.User],
              isStandalonePage: true,
              helpKey: 'contest-list',
            },
            loadComponent: () => {
              const authService = inject(AuthService);
              const role = authService.getSystemRole();
              if (role === SystemRole.User) {
                return import('./components/participant-list/participant-list.component').then(
                  (m) => m.ParticipantListComponent,
                );
              }
              return import('./components/contest-list/contest-list.component').then(
                (m) => m.ContestListComponent,
              );
            },
          },
          {
            path: ':contestId',
            data: {
              breadcrumb: 'breadcrumb.contestWith',
              icon: 'emoji_events',
            },
            children: [
              {
                path: '',
                canActivate: [userRoleGuard, contestRedirectGuard],
                data: {
                  roles: [SystemRole.SuperAdmin, SystemRole.Admin, SystemRole.User],
                },
                children: [],
              },
              {
                path: 'dashboard',
                canActivate: [userRoleGuard],
                data: {
                  roles: [SystemRole.SuperAdmin, SystemRole.Admin, SystemRole.User],
                  contestRoles: [ContestRole.Organizer],
                  breadcrumb: 'nounForms.dashboard.singular',
                  icon: 'dashboard',
                  helpKey: 'contest-dashboard',
                },
                loadComponent: () =>
                  import('./components/contest-dashboard/contest-dashboard.component').then(
                    (m) => m.ContestDashboardComponent,
                  ),
              },
              {
                path: 'podium',
                canActivate: [userRoleGuard],
                data: {
                  roles: [SystemRole.SuperAdmin, SystemRole.Admin, SystemRole.User],
                  contestRoles: [ContestRole.Organizer],
                  breadcrumb: 'adminDashboard.presentation',
                  icon: 'present_to_all',
                  helpKey: 'contest-presentation',
                },
                loadComponent: () =>
                  import('./components/podium-presentation/podium-presentation.component').then(
                    (m) => m.PodiumPresentationComponent,
                  ),
              },
              {
                path: 'setup',
                canActivate: [userRoleGuard],
                data: {
                  roles: [SystemRole.SuperAdmin, SystemRole.Admin, SystemRole.User],
                  contestRoles: [ContestRole.Organizer],
                  breadcrumb: 'breadcrumb.setup',
                  icon: 'settings',
                  helpKey: 'contest-setup',
                },
                loadComponent: () =>
                  import('./components/contest-setup/contest-setup.component').then(
                    (m) => m.ContestSetupComponent,
                  ),
              },
              {
                path: 'run',
                canActivate: [userRoleGuard],
                data: {
                  roles: [SystemRole.SuperAdmin, SystemRole.Admin, SystemRole.User],
                  contestRoles: [ContestRole.Organizer, ContestRole.Judge, ContestRole.Dancer],
                  breadcrumb: 'dashboard.runContest',
                  icon: 'play_arrow',
                  helpKey: 'contest-overview',
                },
                loadComponent: () =>
                  import('./components/contest-overview/contest-overview.component').then(
                    (m) => m.ContestOverviewComponent,
                  ),
              },
              {
                path: 'leaderboard',
                canActivate: [userRoleGuard],
                data: {
                  roles: [SystemRole.SuperAdmin, SystemRole.Admin, SystemRole.User],
                  contestRoles: [ContestRole.Organizer, ContestRole.Judge],
                  breadcrumb: 'labels.leaderboard',
                  icon: 'leaderboard',
                  helpKey: 'leaderboard',
                },
                loadComponent: () =>
                  import('./components/score-leaderboard/score-leaderboard.component').then(
                    (m) => m.ScoreLeaderboardComponent,
                  ),
              },
              {
                path: 'score/:poolId',
                canActivate: [userRoleGuard, activePoolGuard],
                data: {
                  roles: [SystemRole.User],
                  breadcrumb: 'breadcrumb.scoreWith',
                  icon: 'how_to_vote',
                  helpKey: 'score-pool',
                },
                loadComponent: () =>
                  import('./components/score-pool/score-pool.component').then(
                    (m) => m.ScorePoolComponent,
                  ),
              },
              {
                path: 'manage',
                canActivate: [userRoleGuard],
                data: {
                  roles: [SystemRole.SuperAdmin, SystemRole.Admin, SystemRole.User],
                  contestRoles: [ContestRole.Organizer],
                },
                children: [
                  {
                    path: '',
                    redirectTo: '..',
                    pathMatch: 'full',
                  },
                  {
                    path: 'organizers',
                    data: {
                      breadcrumb: 'nounForms.organizer.plural',
                      icon: 'manage_accounts',
                      showFilters: true,
                      isStandalonePage: true,
                      helpKey: 'organizer-list',
                    },
                    loadComponent: () =>
                      import('./components/organizer-list/organizer-list.component').then(
                        (m) => m.OrganizerListComponent,
                      ),
                  },
                  {
                    path: 'dancers',
                    data: {
                      breadcrumb: 'nounForms.dancer.plural',
                      icon: 'accessibility_new',
                      showFilters: true,
                      isStandalonePage: true,
                      helpKey: 'dancer-list',
                    },
                    loadComponent: () =>
                      import('./components/dancer-list/dancer-list.component').then(
                        (m) => m.DancerListComponent,
                      ),
                  },
                  {
                    path: 'judges',
                    data: {
                      breadcrumb: 'nounForms.judge.plural',
                      icon: 'gavel',
                      showFilters: true,
                      isStandalonePage: true,
                      helpKey: 'judge-list',
                    },
                    loadComponent: () =>
                      import('./components/judge-list/judge-list.component').then(
                        (m) => m.JudgeListComponent,
                      ),
                  },
                  {
                    path: 'pools',
                    data: {
                      breadcrumb: 'nounForms.pool.plural',
                      icon: 'pools',
                      showFilters: true,
                      isStandalonePage: true,
                      helpKey: 'pool-list',
                    },
                    loadComponent: () =>
                      import('./components/pool-list/pool-list.component').then(
                        (m) => m.PoolListComponent,
                      ),
                  },
                  {
                    path: 'scores',
                    data: {
                      breadcrumb: 'nounForms.score.plural',
                      icon: 'score',
                      showFilters: true,
                      isStandalonePage: true,
                      helpKey: 'score-list',
                    },
                    loadComponent: () =>
                      import('./components/score-list/score-list.component').then(
                        (m) => m.ScoreListComponent,
                      ),
                  },
                  {
                    path: 'rounds',
                    data: {
                      breadcrumb: 'nounForms.round.plural',
                      icon: 'format_list_numbered',
                      showFilters: true,
                      isStandalonePage: true,
                      helpKey: 'round-list',
                    },
                    loadComponent: () =>
                      import('./components/round-list/round-list.component').then(
                        (m) => m.RoundListComponent,
                      ),
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        path: '**',
        loadComponent: () =>
          import('./components/not-found/not-found.component').then((m) => m.NotFoundComponent),
      },
    ],
  },
];
